import { usePathname } from 'next/navigation';
import React, { useEffect, useState } from 'react';

import { SearchDealerContact } from '@/components/sections/dealer/search-dealer-contact';
import { Button } from '@/components/shared/buttons/button';
import { ButtonGroup } from '@/components/shared/buttons/button-group';
import { SocialMediaShareButtons } from '@/components/shared/buttons/social-media-share-buttons';
import { FormToggle } from '@/components/shared/data-display/toggle';
import { Text } from '@/components/shared/element/text';
import LeadFormMinimal from '@/components/shared/form/lead-form-minimal';
import { Box } from '@/components/shared/layout/box';
import { Container } from '@/components/shared/layout/container';
import { Grid } from '@/components/shared/layout/grid';
import { Section } from '@/components/shared/layout/section';
import { Image } from '@/components/shared/media/image';
import { generateTabletAndMobileImageSources } from '@/components/shared/media/image/generate-sources';
import { ImageProps } from '@/components/shared/media/image/Image.props';
import { Breadcrumb } from '@/components/shared/navigation/breadcrumb';
import { cleanUrl, formatDate } from '@/components/shared/utility/format';
import { MENU_CV_SPECIAL_OFFER } from '@/contents/link';
import { IProvince } from '@/types/dealer/province';
import { IVehicleType } from '@/types/services/contentstack';
import { IModel } from '@/types/services/lead';
import { IButton } from '@/types/shared';

export interface OfferDetailProps {
  children?: React.ReactNode;
  image_detail?: ImageProps;
  title: string;
  beginDate: string;
  endDate: string;
  detail: string;
  remark?: string;
  buttons?: IButton[];
  category?: {
    name: string;
    url: string;
  };
  form?: boolean;
  campaignName: string;
  campaignId: string;
  campaignVersion: string;
  provinces?: IProvince[];
  serviceProvinces?: IProvince[];
  lcvModel?: IModel;
  fbModel?: string;
  vehicleType?: IVehicleType;
  dealerSearch?: {
    isActive?: boolean;
    title?: string;
  };
}

const OfferListButton: React.FC = () => {
  return (
    <ButtonGroup>
      <Button
        label="ดูข้อเสนอทั้งหมด"
        icon="arrow-back"
        variant="button-gray"
        border
        href={MENU_CV_SPECIAL_OFFER.url}
      />
    </ButtonGroup>
  );
};
OfferListButton.displayName = 'OfferListButton';

const CustomButtons: React.FC<{ buttons: IButton[]; title: string }> = ({
  buttons,
  title,
}) => {
  return (
    <ButtonGroup>
      {buttons.map((o, i) => (
        <Button
          key={i}
          variant="button-gray"
          border={o.type === 'border'}
          label={o.label}
          icon={o.icon}
          href={cleanUrl(`/${o.url}`)}
          target={o.target}
          css={{ '@maxlg': { width: '100%' } }}
          tracking={{
            dataTrack: 'offer-detail',
            dataTrackText: o.label,
            dataTrackUrl: o.url,
            dataTrackValue: title,
          }}
        />
      ))}
    </ButtonGroup>
  );
};
CustomButtons.displayName = 'CustomButtons';

const OfferDetailSection: React.FC<OfferDetailProps> = (
  props: OfferDetailProps
) => {
  const [currentURL, setCurrentURL] = useState<string>('');
  const pathname = usePathname();

  useEffect(() => {
    setCurrentURL(location.origin + location.pathname);
  }, [pathname]);

  return (
    <>
      <Breadcrumb
        items={[
          {
            label: MENU_CV_SPECIAL_OFFER.label,
            url: MENU_CV_SPECIAL_OFFER.url,
          },
          {
            label: props.category?.name,
            url: `${MENU_CV_SPECIAL_OFFER.url}/${props.category?.url}`,
          },
          {
            label: props.title,
          },
        ]}
        css={{ display: 'none' }}
      />
      <Section size="xl" test="section_offer_detail">
        <Container>
          <Grid
            templateColumns={{ '@lg': '12' }}
            alignItems="start"
            columnGap="2"
            rowGap={{ '@initial': '5', '@lg': '7' }}
          >
            <Grid
              rowGap={{ '@initial': '5', '@lg': '8' }}
              css={{ '@lg': { gridColumn: '1 / span 6' } }}
            >
              {!!props.title && (
                <Grid rowGap="2" css={{ display: 'none' }}>
                  {!!props.title && (
                    <Text
                      as="h1"
                      size="h4"
                      font="bold"
                      wrap
                      dangerouslySetInnerHTML={{ __html: props.title }}
                    />
                  )}
                  {!!props.beginDate && !!props.endDate && (
                    <Text as="p" size="h5" font="bold" variant="text-red">
                      {formatDate(props.beginDate)} &ndash;{' '}
                      {formatDate(props.endDate)}
                    </Text>
                  )}
                </Grid>
              )}
              {!!props.image_detail?.src && (
                <Box border="1" borderColor="gray300">
                  <Image
                    priority
                    src={props.image_detail.src}
                    alt={props.image_detail.alt}
                    width={props.image_detail.width}
                    height={props.image_detail.height}
                    layout="responsive"
                    sources={[
                      {
                        srcset: props.image_detail.src,
                        media: { minWidth: 1024 },
                        options: { width: 640 },
                      },
                      ...generateTabletAndMobileImageSources({
                        mobileSrc: props.image_detail.src,
                      }),
                    ]}
                  />
                </Box>
              )}
              {!!props.form && (
                <FormToggle
                  title="เงื่อนไขของรายการ"
                  detail={props.detail}
                  remark={props.remark}
                />
              )}
              {!!props.dealerSearch?.isActive && (
                <Box display={{ '@maxlg': 'none' }}>
                  <SearchDealerContact
                    isActive={props.dealerSearch?.isActive}
                    title={props.dealerSearch?.title as string}
                    provinces={props.serviceProvinces as IProvince[]}
                    vehicleType={props.vehicleType as IVehicleType}
                  />
                </Box>
              )}
              {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
              {/* @ts-ignore */}
              {!!props.form && props?.buttons?.length > 0 && (
                <Box display={{ '@maxlg': 'none' }}>
                  <CustomButtons
                    buttons={props.buttons as IButton[]}
                    title={props.title}
                  />
                </Box>
              )}
              <Box display={{ '@maxlg': 'none' }}>
                <SocialMediaShareButtons
                  id="copy-link-desktop"
                  title={props.title}
                  url={currentURL}
                  dataTrack="offer-detail"
                />
              </Box>
              <Box display={{ '@maxlg': 'none' }}>
                <OfferListButton />
              </Box>
            </Grid>
            <Grid rowGap="8" css={{ '@lg': { gridColumn: '8 / span 5' } }}>
              {props.form ? (
                <LeadFormMinimal
                  title="ลงทะเบียนรับข้อเสนอนี้"
                  campaignName={props.campaignName}
                  campaignId={props.campaignId}
                  campaignVersion={props.campaignVersion}
                  provinces={props.provinces}
                  successURL={`${currentURL}/thankyou`}
                  lcvModel={props.lcvModel}
                  fbModel={props.fbModel}
                  vehicleType={props.vehicleType}
                  theme="light"
                />
              ) : (
                <>
                  <FormToggle
                    title="เงื่อนไขของรายการ"
                    detail={props.detail}
                    remark={props.remark}
                    variant="transparent"
                    isShow
                  />
                  {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                  {/* @ts-ignore */}
                  {props?.buttons?.length > 0 && (
                    <ButtonGroup>
                      {props.buttons?.map((o, i) => (
                        <Button
                          key={i}
                          border={o.type == 'border'}
                          label={o.label}
                          icon={o.icon}
                          href={cleanUrl(`/${o.url}`)}
                          target={o.target}
                          tracking={{
                            dataTrack: 'offer-detail',
                            dataTrackText: o.label,
                            dataTrackUrl: o.url,
                            dataTrackValue: props.title,
                          }}
                        />
                      ))}
                    </ButtonGroup>
                  )}
                </>
              )}
            </Grid>
            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
            {/* @ts-ignore */}
            {!!props.form && props?.buttons?.length > 0 && (
              <Box display={{ '@lg': 'none' }} width="full">
                <CustomButtons
                  buttons={props.buttons as IButton[]}
                  title={props.title}
                />
              </Box>
            )}
            {!!props.dealerSearch?.isActive && (
              <Box display={{ '@lg': 'none' }}>
                <SearchDealerContact
                  isActive={props.dealerSearch?.isActive}
                  title={props.dealerSearch?.title as string}
                  provinces={props.serviceProvinces as IProvince[]}
                  vehicleType={props.vehicleType as IVehicleType}
                  dataTrack="offer-detail"
                />
              </Box>
            )}
            <Box display={{ '@lg': 'none' }}>
              <SocialMediaShareButtons
                id="copy-text-mobile"
                title={props.title}
                url={currentURL}
                dataTrack="offer-detail"
              />
            </Box>
            <Box display={{ '@lg': 'none' }}>
              <OfferListButton />
            </Box>
          </Grid>
        </Container>
      </Section>
    </>
  );
};
OfferDetailSection.displayName = 'OfferDetailSection';
export default OfferDetailSection;
